import React, {
  useState, useEffect, useRef, useContext,
} from 'react';
import { stub as $t } from '@nbcnews/analytics-framework';

import { IconClose } from 'components/Icon/Close';
import Link from 'components/Link';
import { Save } from 'components/Social/SocialShareMenu/Save';

import { useDispatch, useSelector } from 'react-redux';
import { loadMostPopularCuratedList, loadMostPopularRecipes } from 'redux/modules/navbar';

import { FeatureFlagContext } from 'lib/ContextTypes';
import { getAuthenticationState } from 'lib/authenticationUtils';
import {
  UNAUTHENTICATED,
} from 'lib/myNewsConstants';

import styles from './styles.module.scss';

$t('register', 'trending_nav');

const drawnPath = 'M22.663 17.743c0-.585-.096-.71-.688-1.034-.012-.007-.419-.224-.552-.299a6.674 6.674 0 0 1-.677-.428c-1.816-1.322-2.718-2.686-2.718-7.315-.289-2.406-1.826-5.037-4.5-5.905l-.829-.27.149-.813c.02-.112.029-.183.029-.24 0-.794-.69-1.439-1.545-1.439-.856 0-1.546.645-1.546 1.438 0 .058.009.129.03.24l.097.93-.777.154c-2.674.868-4.44 3.499-4.5 5.905 0 4.629-.903 5.993-2.719 7.315-.21.153-.426.288-.676.428-.134.075-.54.292-.553.3-.592.322-.688.448-.688 1.033 0 .714 3.255 1.887 7.882 2.263l1.136.093-.212 1.055a2.25 2.25 0 0 0-.05.444c0 1.325 1.152 2.402 2.576 2.402 1.424 0 2.575-1.077 2.575-2.402a2.25 2.25 0 0 0-.05-.444l-.211-1.055 1.135-.093c4.628-.376 7.882-1.549 7.882-2.263Z';
/**
 * Trending Nav
 * This component will display a list of popular stories, products, and recipes
 * from TODAY.
 * @param {object} root0
 * @param {string} root0.pageRoute
 * @returns {React.Element} The Trending Nav component
 */
function TrendingNav({ pageRoute }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef(null);
  const dispatch = useDispatch();
  const authenticationState = getAuthenticationState();
  /**
   * Toggles the menu open or closed
   */
  const toggleMenu = () => {
    const trackingAction = menuOpen ? 'close' : 'open';
    $t('track', 'trending_nav', { action: trackingAction });
    setMenuOpen(!menuOpen);
  };
  /**
   * Sets the menuOpen state to false, closing the menu
   */
  const closeMenu = () => {
    $t('track', 'trending_nav', { action: 'close' });
    setMenuOpen(false);
  };

  /**
   * Handles the Escape keydown event
   * @param {KeyboardEvent} e The keydown event
   */
  const handleEscapeKey = (e) => {
    if (e.key === 'Escape') {
      closeMenu();
    }
  };

  /**
   * Attaches the event listeners for the trending nav
   * @function attachAccordionListeners
   * @description Attaches event listeners for keydown for the Escape key
   */
  const attachAccordionListeners = () => {
    window.addEventListener('keydown', handleEscapeKey);
  };

  /**
   * Removes the event listeners for the trending nav
   * @function removeAccordionListeners
   * @description Removes event listeners for keydown for the Escape key
   */
  const removeAccordionListeners = () => {
    window.removeEventListener('keydown', handleEscapeKey);
  };

  useEffect(() => {
    attachAccordionListeners();
    return () => removeAccordionListeners();
  }, [menuOpen]);


  useEffect(() => {
    dispatch(loadMostPopularCuratedList({ id: 'tdcl00055667799' }));
    dispatch(loadMostPopularCuratedList({ id: 'tdcl00077889900', type: 'shop' }));
    dispatch(loadMostPopularRecipes());
  }, []);

  const mostPopular = useSelector((state) => state.navbar.mostPopular);
  const darkTheme = useSelector((state) => state.navbar.theme) === 'dark';

  const {
    'trending-nav-show-general': trendingNavShowGeneral,
  } = useContext(FeatureFlagContext);

  const { read, shop, recipe } = mostPopular || {};
  const filteredMostPopular = [
    ...(shop !== undefined ? [shop] : []),
    ...(recipe !== undefined ? [recipe] : []),
  ];

  const arrayOfTrending = filteredMostPopular;

  // Add read to the array of trending items if the feature flag is enabled
  if (trendingNavShowGeneral && read !== undefined) {
    arrayOfTrending.unshift(read);
  }

  if (!arrayOfTrending.length) {
    return null;
  }

  // We need to hide the save icons withing the trending nav on the account page if the user is not
  // authenticated because users are not able to save items through the trending nav on the account page
  // if they are logged out. The request to save an item through the trending nav gets cut off
  // when a user logs in and the page switches from the account page to the dashboard page.
  const isUnauthenticated = authenticationState === UNAUTHENTICATED;
  const hideTrendingNavSave = pageRoute === '/account' && isUnauthenticated;

  return (
    <div
      className={styles.trendingNavContainer}
      data-testid="trendingNavContainer"
      ref={navRef}
    >
      <button
        className={`${styles.trendingNavButton} ${menuOpen ? styles.open : ''} ${darkTheme ? styles.darkTheme : ''}`}
        onClick={toggleMenu}
        data-testid="trendingNavButton"
        type="button"
        aria-label={menuOpen ? 'Close Trending Nav Menu' : 'Open Trending Nav Menu'}
      >
        <svg width="23" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fillRule="evenodd" clipRule="evenodd" d={drawnPath} />
        </svg>

      </button>
      {menuOpen
        && (
          <nav
            className={styles.trendingNav}
            data-testid="trendingNavMenu"
          >
            <header className={styles.trendingHeader}>
              <button
                className={styles.closeButton}
                onClick={closeMenu}
                type="button"
                data-testid="trendingNavCloseButton"
              >
                <IconClose />
              </button>
            </header>
            <div className={styles.sectionsContainer} data-testid="trendingSection">

              {arrayOfTrending.map((obj, i) => (
                <section
                  className={styles.trendingSection}
                  key={`obj.title-${obj.title}`}
                  data-testid={`trendingSection-${i}`}
                  data-activity-map={obj.dataActivityMap}
                >
                  <h4 className={styles.trendingSectionTitle}>{obj.title}</h4>
                  {obj.items.map((trendingItem) => (
                    <div className={styles.trendingOption}>
                      {!hideTrendingNavSave && (
                        <div className={styles.bookmarkContainer}>
                          <Save
                            contentId={trendingItem.id}
                            contentType={trendingItem.contentType}
                            additionalClasses={styles.trendingOptionSave}
                          />
                        </div>
                      )}
                      <Link
                        className={styles.trendingOptionLink}
                        href={trendingItem.url}
                      >
                        <div className={styles.linkContainer}>

                          {trendingItem.headline}
                        </div>
                      </Link>

                    </div>
                  ))}
                </section>
              ))}

            </div>
          </nav>

        )}
    </div>
  );
}

export { TrendingNav };
